import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, getErrorMessage } from '../services/api';
import { Runner, RunnerStats, DCAState, ScriptedRunnerState, Trade, ExchangeCredential, RunnerLog, GridLevel } from '../types';
import { GridLevelEditor } from '../components/GridLevelEditor';

const PAGE_SIZE = 10;

export function RunnerDetail() {
  const navigate = useNavigate();
  const { runnerId } = useParams<{ runnerId: string }>();
  const [runner, setRunner] = useState<Runner | null>(null);
  const [stats, setStats] = useState<RunnerStats | null>(null);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [credential, setCredential] = useState<ExchangeCredential | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedTradeId, setExpandedTradeId] = useState<number | null>(null);
  const [logs, setLogs] = useState<RunnerLog[]>([]);
  const [showLogs, setShowLogs] = useState(true);
  const logsEndRef = React.useRef<HTMLDivElement>(null);
  const [gridBuyLevels, setGridBuyLevels] = useState<GridLevel[]>([]);
  const [gridSellLevels, setGridSellLevels] = useState<GridLevel[]>([]);

  const loadData = useCallback(async (showLoading = true) => {
    if (!runnerId) return;

    try {
      if (showLoading) {
        setIsLoading(true);
      }
      const runnerIdNum = parseInt(runnerId, 10);

      // Load runner, trades, credentials, and logs in parallel
      const [runnerData, tradesData, credentials, logsData] = await Promise.all([
        api.getRunner(runnerIdNum),
        api.getRunnerTrades(runnerIdNum),
        api.getExchangeCredentials(),
        api.getRunnerLogs(runnerIdNum, 100),
      ]);

      setRunner(runnerData);
      setTrades(tradesData);
      setLogs(logsData);

      // Find the credential for this runner
      if (runnerData.exchangeCredentialId) {
        const cred = credentials.find(c => c.exchangeCredentialId === runnerData.exchangeCredentialId);
        setCredential(cred || null);
      }

      // Load stats
      const stateResponse = await api.getRunnerState(runnerIdNum);
      if (stateResponse?.stateJson) {
        try {
          const rawState = JSON.parse(stateResponse.stateJson) as Record<string, unknown>;

          // Check if this is a scripted runner state (has scriptState property)
          if (rawState.scriptState !== undefined) {
            const scriptState = (rawState.scriptState || {}) as Record<string, unknown>;
            const lastPrice = (rawState.lastPrice as number) || 0;

            // Support multiple field naming conventions that scripts might use
            const totalQuantity =
              (scriptState.totalQuantity as number) ||
              (scriptState.position as number) ||
              (scriptState.quantity as number) ||
              0;
            const totalCost =
              (scriptState.totalCost as number) ||
              (scriptState.cost as number) ||
              0;
            const averagePrice =
              (scriptState.averagePrice as number) ||
              (scriptState.avgPrice as number) ||
              (totalQuantity > 0 ? totalCost / totalQuantity : 0);

            const currentValue = totalQuantity * lastPrice;
            const unrealizedPnL = currentValue - totalCost;
            const unrealizedPnLPercent = totalCost > 0 ? (unrealizedPnL / totalCost) * 100 : 0;

            setStats({
              currentPosition: totalQuantity,
              currentPrice: lastPrice,
              currentValue: currentValue,
              unrealizedPnL: unrealizedPnL,
              unrealizedPnLPercent: unrealizedPnLPercent,
              averagePrice: averagePrice,
              lastUpdated: stateResponse.lastUpdated,
              basePrecision: stateResponse.basePrecision,
              quotePrecision: stateResponse.quotePrecision,
            });
          } else {
            // Standard DCA state format
            const state = rawState as unknown as DCAState;
            setStats({
              currentPosition: state.totalQuantityHeld,
              currentPrice: state.lastPrice,
              currentValue: state.currentValue,
              unrealizedPnL: state.unrealizedPnL,
              unrealizedPnLPercent: state.unrealizedPnLPercent,
              averagePrice: state.averagePrice,
              lastUpdated: stateResponse.lastUpdated,
              basePrecision: stateResponse.basePrecision,
              quotePrecision: stateResponse.quotePrecision,
            });
          }
          // Parse grid levels if present
          if (rawState.scriptState !== undefined) {
            const ss = (rawState.scriptState || {}) as Record<string, unknown>;
            if (Array.isArray(ss.buyLevels)) {
              setGridBuyLevels(ss.buyLevels as GridLevel[]);
            } else {
              setGridBuyLevels([]);
            }
            if (Array.isArray(ss.sellLevels)) {
              setGridSellLevels(ss.sellLevels as GridLevel[]);
            } else {
              setGridSellLevels([]);
            }
          } else {
            setGridBuyLevels([]);
            setGridSellLevels([]);
          }
        } catch {
          setStats(null);
          setGridBuyLevels([]);
          setGridSellLevels([]);
        }
      } else {
        setStats(null);
        setGridBuyLevels([]);
        setGridSellLevels([]);
      }

      setError('');
    } catch (err: unknown) {
      // Only show error on initial load, not on refresh
      if (showLoading) {
        setError(`Failed to load runner: ${getErrorMessage(err)}`);
      }
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  }, [runnerId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Poll for updates if runner is active
  useEffect(() => {
    if (!runner || runner.status !== 'Active') return;

    const interval = setInterval(() => {
      void loadData(false); // Don't show loading state on refresh
    }, 10000);

    return () => clearInterval(interval);
  }, [runner, loadData]);

  const handleStartRunner = async () => {
    if (!runner) return;
    try {
      await api.startRunner(runner.runnerId);
      await loadData();
    } catch (err: unknown) {
      setError(`Failed to start runner: ${getErrorMessage(err)}`);
    }
  };

  const handleStopRunner = async () => {
    if (!runner) return;
    try {
      await api.stopRunner(runner.runnerId);
      await loadData();
    } catch (err: unknown) {
      setError(`Failed to stop runner: ${getErrorMessage(err)}`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return '#4caf50';
      case 'Paused': return '#ff9800';
      case 'Inactive': return '#9e9e9e';
      default: return '#9e9e9e';
    }
  };

  const getSideColor = (side: string) => {
    return side.toLowerCase() === 'buy' ? '#4caf50' : '#f44336';
  };

  const getTradeStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'filled': return '#4caf50';
      case 'partiallyfilled': return '#ff9800';
      case 'placed': return '#2196f3';
      case 'cancelled': return '#9e9e9e';
      default: return '#8892b0';
    }
  };

  const getPnLColor = (pnl: number | null | undefined) => {
    if (pnl === null || pnl === undefined) return '#8892b0';
    if (pnl > 0) return '#4caf50';
    if (pnl < 0) return '#f44336';
    return '#8892b0';
  };

  const getLogLevelColor = (level: string) => {
    switch (level) {
      case 'Error': return '#f44336';
      case 'Warning': return '#ff9800';
      case 'Info':
      default: return '#8892b0';
    }
  };

  const formatLogTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  const formatNumber = (num: number | null | undefined, decimals = 2) => {
    if (num === null || num === undefined) return '-';
    if (num === 0) return '0';
    if (Math.abs(num) < 0.01) return num.toFixed(6);
    return num.toFixed(decimals);
  };

  const formatCurrency = (num: number | null | undefined, currency: string, precision = 2) => {
    if (num === null || num === undefined) return '-';
    return `${formatNumber(num, precision)} ${currency}`;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  // Parse configJson into an object
  const parseConfig = (configJson: string | null): Record<string, unknown> | null => {
    if (!configJson) return null;
    try {
      return JSON.parse(configJson) as Record<string, unknown>;
    } catch {
      return null;
    }
  };

  // Format camelCase/PascalCase to readable label
  const formatConfigLabel = (key: string): string => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  // Format config value for display
  const isObjectArray = (value: unknown): value is Record<string, unknown>[] =>
    Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null;

  const formatConfigValue = (value: unknown): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'string') return value || '-';
    if (typeof value === 'object' && !Array.isArray(value)) {
      return Object.entries(value as Record<string, unknown>)
        .map(([k, v]) => `${formatConfigLabel(k)}: ${formatConfigValue(v)}`)
        .join(', ');
    }
    if (Array.isArray(value)) {
      return value.map(v => formatConfigValue(v)).join(', ');
    }
    return JSON.stringify(value);
  };

  // Check if runner is a scripted runner
  const isScriptedRunner = runner?.className === 'ScriptedSpotRunner' || runner?.className === 'ScriptedFuturesRunner';

  const config = runner ? parseConfig(runner.configJson) : null;

  // Pagination
  const totalPages = Math.ceil(trades.length / PAGE_SIZE);
  const paginatedTrades = trades.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const toggleTradeExpand = (tradeId: number) => {
    setExpandedTradeId(expandedTradeId === tradeId ? null : tradeId);
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading...</div>
      </div>
    );
  }

  if (!runner) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>Runner not found</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={() => navigate('/')} style={styles.backButton}>
            &larr; Back
          </button>
          <h1 style={styles.title}>{runner.displayName}</h1>
          <span style={{ ...styles.statusBadge, backgroundColor: getStatusColor(runner.status) }}>
            {runner.status}
          </span>
        </div>
        <div style={styles.headerActions}>
          {runner.status === 'Inactive' && (
            <>
              <button onClick={() => { void handleStartRunner(); }} style={styles.startButton}>
                Start
              </button>
              <button onClick={() => navigate(`/runner/${runner.runnerId}`)} style={styles.editButton}>
                Edit
              </button>
            </>
          )}
          {runner.status === 'Active' && (
            <button onClick={() => { void handleStopRunner(); }} style={styles.stopButton}>
              Stop
            </button>
          )}
        </div>
      </header>

      <main style={styles.main}>
        {error && <div style={styles.errorMessage}>{error}</div>}

        {/* Runner Info Section */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Runner Info</h2>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Symbol</span>
              <span style={styles.infoValue}>{runner.symbol}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Exchange</span>
              <span style={styles.infoValue}>{credential?.exchangeName || 'Not configured'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Type</span>
              <span style={styles.infoValue}>{runner.runnerType}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Strategy</span>
              <span style={styles.infoValue}>{runner.className}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Investment</span>
              <span style={styles.infoValue}>{runner.startingInvestment} {runner.baseCurrency}</span>
            </div>
            {runner.intervalHours && (
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Interval</span>
                <span style={styles.infoValue}>{runner.intervalHours}h</span>
              </div>
            )}
          </div>
        </section>

        {/* Stats Section */}
        {stats && (
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Live Stats</h2>
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <span style={styles.statLabel}>Position</span>
                <span style={styles.statValue}>
                  {formatNumber(stats.currentPosition, stats.basePrecision)}
                </span>
              </div>
              <div style={styles.statCard}>
                <span style={styles.statLabel}>Current Price</span>
                <span style={styles.statValue}>
                  {formatCurrency(stats.currentPrice, runner.baseCurrency, stats.quotePrecision)}
                </span>
              </div>
              <div style={styles.statCard}>
                <span style={styles.statLabel}>Current Value</span>
                <span style={styles.statValue}>
                  {formatCurrency(stats.currentValue, runner.baseCurrency, stats.quotePrecision)}
                </span>
              </div>
              <div style={styles.statCard}>
                <span style={styles.statLabel}>Unrealized P&L</span>
                <span style={{ ...styles.statValue, color: getPnLColor(stats.unrealizedPnL) }}>
                  {stats.unrealizedPnL >= 0 ? '+' : ''}
                  {formatCurrency(stats.unrealizedPnL, runner.baseCurrency, stats.quotePrecision)}
                  {' '}({stats.unrealizedPnLPercent >= 0 ? '+' : ''}{formatNumber(stats.unrealizedPnLPercent)}%)
                </span>
              </div>
              <div style={styles.statCard}>
                <span style={styles.statLabel}>Average Price</span>
                <span style={styles.statValue}>
                  {formatCurrency(stats.averagePrice, runner.baseCurrency, stats.quotePrecision)}
                </span>
              </div>
              {stats.lastUpdated && (
                <div style={styles.statCard}>
                  <span style={styles.statLabel}>Last Updated</span>
                  <span style={styles.statValue}>{formatDate(stats.lastUpdated)}</span>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Grid Level Editor Section */}
        {(gridBuyLevels.length > 0 || gridSellLevels.length > 0) && (
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Grid Levels</h2>
            <GridLevelEditor
              runnerId={parseInt(runnerId!, 10)}
              buyLevels={gridBuyLevels}
              sellLevels={gridSellLevels}
              isActive={runner.status === 'Active'}
              quoteCurrency={runner.baseCurrency}
              onSaved={() => { void loadData(false); }}
            />
          </section>
        )}

        {/* Configuration Section */}
        {config && Object.keys(config).length > 0 && (
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Configuration</h2>
            <div style={styles.configGrid}>
              {Object.entries(config).filter(([, value]) => !isObjectArray(value)).map(([key, value]) => (
                <div key={key} style={styles.configCard}>
                  <span style={styles.configLabel}>{formatConfigLabel(key)}</span>
                  <span style={styles.configValue}>{formatConfigValue(value)}</span>
                </div>
              ))}
            </div>
            {Object.entries(config).filter(([, value]) => isObjectArray(value)).map(([key, value]) => (
              <div key={key} style={{ marginTop: '16px' }}>
                <span style={styles.configLabel}>{formatConfigLabel(key)}</span>
                <table style={styles.configTable}>
                  <thead>
                    <tr>
                      {Object.keys((value as Record<string, unknown>[])[0]).map(col => (
                        <th key={col} style={styles.configTh}>{formatConfigLabel(col)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(value as Record<string, unknown>[]).map((row, i) => (
                      <tr key={i}>
                        {Object.values(row).map((cell, j) => (
                          <td key={j} style={styles.configTd}>{formatConfigValue(cell)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </section>
        )}

        {/* Script Section */}
        {isScriptedRunner && runner.scriptCode && (
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Script</h2>
            <pre style={styles.scriptContainer}>
              <code style={styles.scriptCode}>{runner.scriptCode}</code>
            </pre>
          </section>
        )}

        {/* Logs Section - Only show for scripted runners */}
        {isScriptedRunner && (
          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Script Logs</h2>
              <div style={styles.logActions}>
                <span style={styles.logCount}>{logs.length} logs</span>
                <button
                  onClick={() => setShowLogs(!showLogs)}
                  style={styles.toggleButton}
                >
                  {showLogs ? 'Hide' : 'Show'}
                </button>
                <button
                  onClick={() => setLogs([])}
                  style={styles.clearButton}
                >
                  Clear
                </button>
              </div>
            </div>

            {showLogs && (
              <div style={styles.logsContainer}>
                {logs.length === 0 ? (
                  <div style={styles.emptyLogs}>No logs yet</div>
                ) : (
                  logs.map((log) => (
                    <div key={log.runnerLogId} style={styles.logEntry}>
                      <span style={styles.logTime}>{formatLogTime(log.timestamp)}</span>
                      <span style={{ ...styles.logLevel, color: getLogLevelColor(log.level) }}>
                        [{log.level}]
                      </span>
                      <span style={styles.logMessage}>{log.message}</span>
                    </div>
                  ))
                )}
                <div ref={logsEndRef} />
              </div>
            )}
          </section>
        )}

        {/* Trades Section */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Trade History</h2>
            <span style={styles.tradeCount}>{trades.length} trades</span>
          </div>

          {trades.length === 0 ? (
            <div style={styles.emptyTrades}>No trades yet</div>
          ) : (
            <>
              <div style={styles.tradesTable}>
                <div style={styles.tableHeader}>
                  <span style={styles.colDate}>Date</span>
                  <span style={styles.colSide}>Side</span>
                  <span style={styles.colType}>Type</span>
                  <span style={styles.colQuantity}>Quantity</span>
                  <span style={styles.colPrice}>Price</span>
                  <span style={styles.colFees}>Fees</span>
                  <span style={styles.colStatus}>Status</span>
                </div>

                {paginatedTrades.map((trade) => (
                  <React.Fragment key={trade.tradeId}>
                    <div
                      style={{
                        ...styles.tableRow,
                        cursor: trade.fills.length > 0 ? 'pointer' : 'default',
                      }}
                      onClick={() => trade.fills.length > 0 && toggleTradeExpand(trade.tradeId)}
                    >
                      <span style={styles.colDate}>{formatDate(trade.createdAt)}</span>
                      <span style={{ ...styles.colSide, color: getSideColor(trade.side) }}>
                        {trade.side}
                      </span>
                      <span style={styles.colType}>{trade.orderType}</span>
                      <span style={styles.colQuantity}>
                        {formatNumber(trade.totalFilledQuantity || trade.requestedQuantity, 6)}
                      </span>
                      <span style={styles.colPrice}>
                        {trade.averageFilledPrice
                          ? formatNumber(trade.averageFilledPrice, 4)
                          : trade.requestedPrice
                            ? formatNumber(trade.requestedPrice, 4)
                            : 'Market'}
                      </span>
                      <span style={styles.colFees}>
                        {formatNumber(trade.totalFees, 4)}
                      </span>
                      <span style={{ ...styles.colStatus, color: getTradeStatusColor(trade.status) }}>
                        {trade.status}
                        {trade.fills.length > 0 && (
                          <span style={styles.expandIcon}>
                            {expandedTradeId === trade.tradeId ? ' ▼' : ' ▶'}
                          </span>
                        )}
                      </span>
                    </div>

                    {/* Expanded fills */}
                    {expandedTradeId === trade.tradeId && trade.fills.length > 0 && (
                      <div style={styles.fillsContainer}>
                        <div style={styles.fillsHeader}>
                          <span style={styles.fillColTime}>Time</span>
                          <span style={styles.fillColQty}>Quantity</span>
                          <span style={styles.fillColPrice}>Price</span>
                          <span style={styles.fillColFee}>Fee</span>
                          <span style={styles.fillColLiquidity}>Liquidity</span>
                        </div>
                        {trade.fills.map((fill) => (
                          <div key={fill.tradeFillId} style={styles.fillRow}>
                            <span style={styles.fillColTime}>{formatDate(fill.timestamp)}</span>
                            <span style={styles.fillColQty}>{formatNumber(fill.quantity, 6)}</span>
                            <span style={styles.fillColPrice}>{formatNumber(fill.price, 4)}</span>
                            <span style={styles.fillColFee}>
                              {formatNumber(fill.fee, 6)} {fill.feeCurrency}
                            </span>
                            <span style={styles.fillColLiquidity}>{fill.liquidity || '-'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={styles.pagination}>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    style={{
                      ...styles.pageButton,
                      opacity: currentPage === 1 ? 0.5 : 1,
                    }}
                  >
                    Previous
                  </button>
                  <span style={styles.pageInfo}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    style={{
                      ...styles.pageButton,
                      opacity: currentPage === totalPages ? 0.5 : 1,
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </main>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1a1a2e',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 32px',
    backgroundColor: '#16213e',
    borderBottom: '1px solid #233554',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  backButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #8892b0',
    backgroundColor: 'transparent',
    color: '#8892b0',
    cursor: 'pointer',
    fontSize: '14px',
  },
  title: {
    color: '#ccd6f6',
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '12px',
    color: 'white',
    fontWeight: 'bold',
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
  },
  startButton: {
    padding: '8px 20px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#4caf50',
    color: 'white',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  stopButton: {
    padding: '8px 20px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#f44336',
    color: 'white',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  editButton: {
    padding: '8px 20px',
    borderRadius: '6px',
    border: '1px solid #8892b0',
    backgroundColor: 'transparent',
    color: '#8892b0',
    fontSize: '14px',
    cursor: 'pointer',
  },
  main: {
    padding: '32px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  loading: {
    color: '#8892b0',
    textAlign: 'center',
    padding: '48px',
  },
  error: {
    color: '#e94560',
    textAlign: 'center',
    padding: '48px',
  },
  errorMessage: {
    backgroundColor: 'rgba(233, 69, 96, 0.2)',
    border: '1px solid #e94560',
    color: '#e94560',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '24px',
  },
  section: {
    backgroundColor: '#16213e',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    border: '1px solid #233554',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  sectionTitle: {
    color: '#ccd6f6',
    fontSize: '18px',
    fontWeight: 'bold',
    margin: 0,
  },
  tradeCount: {
    color: '#8892b0',
    fontSize: '14px',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '16px',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  infoLabel: {
    color: '#8892b0',
    fontSize: '12px',
    textTransform: 'uppercase',
  },
  infoValue: {
    color: '#ccd6f6',
    fontSize: '16px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '16px',
  },
  statCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '12px',
    backgroundColor: 'rgba(35, 53, 84, 0.5)',
    borderRadius: '8px',
  },
  statLabel: {
    color: '#8892b0',
    fontSize: '11px',
    textTransform: 'uppercase',
  },
  statValue: {
    color: '#ccd6f6',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  emptyTrades: {
    color: '#8892b0',
    textAlign: 'center',
    padding: '32px',
  },
  tradesTable: {
    display: 'flex',
    flexDirection: 'column',
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '180px 60px 80px 100px 100px 80px 100px',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: 'rgba(35, 53, 84, 0.5)',
    borderRadius: '8px 8px 0 0',
    color: '#8892b0',
    fontSize: '12px',
    textTransform: 'uppercase',
    fontWeight: 'bold',
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '180px 60px 80px 100px 100px 80px 100px',
    gap: '8px',
    padding: '12px 16px',
    borderBottom: '1px solid #233554',
    color: '#ccd6f6',
    fontSize: '14px',
    transition: 'background-color 0.2s',
  },
  colDate: {},
  colSide: {
    fontWeight: 'bold',
  },
  colType: {},
  colQuantity: {},
  colPrice: {},
  colFees: {},
  colStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  expandIcon: {
    fontSize: '10px',
    color: '#8892b0',
  },
  fillsContainer: {
    backgroundColor: 'rgba(35, 53, 84, 0.3)',
    padding: '8px 16px 8px 48px',
    borderBottom: '1px solid #233554',
  },
  fillsHeader: {
    display: 'grid',
    gridTemplateColumns: '160px 100px 100px 120px 80px',
    gap: '8px',
    padding: '8px 0',
    color: '#8892b0',
    fontSize: '11px',
    textTransform: 'uppercase',
  },
  fillRow: {
    display: 'grid',
    gridTemplateColumns: '160px 100px 100px 120px 80px',
    gap: '8px',
    padding: '6px 0',
    color: '#a8b2d1',
    fontSize: '13px',
  },
  fillColTime: {},
  fillColQty: {},
  fillColPrice: {},
  fillColFee: {},
  fillColLiquidity: {},
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #233554',
  },
  pageButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #8892b0',
    backgroundColor: 'transparent',
    color: '#8892b0',
    cursor: 'pointer',
    fontSize: '14px',
  },
  pageInfo: {
    color: '#8892b0',
    fontSize: '14px',
  },
  configGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '16px',
  },
  configCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '12px',
    backgroundColor: 'rgba(35, 53, 84, 0.5)',
    borderRadius: '8px',
  },
  configLabel: {
    color: '#8892b0',
    fontSize: '11px',
    textTransform: 'uppercase',
  },
  configValue: {
    color: '#ccd6f6',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  configTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginTop: '8px',
    backgroundColor: 'rgba(35, 53, 84, 0.5)',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  configTh: {
    textAlign: 'left' as const,
    padding: '8px 12px',
    color: '#8892b0',
    fontSize: '11px',
    textTransform: 'uppercase' as const,
    borderBottom: '1px solid rgba(136, 146, 176, 0.2)',
  },
  configTd: {
    padding: '8px 12px',
    color: '#ccd6f6',
    fontSize: '14px',
    borderBottom: '1px solid rgba(136, 146, 176, 0.1)',
  },
  scriptContainer: {
    backgroundColor: '#0d1117',
    borderRadius: '8px',
    padding: '16px',
    margin: 0,
    overflow: 'auto',
    maxHeight: '500px',
    border: '1px solid #233554',
  },
  scriptCode: {
    color: '#c9d1d9',
    fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
    fontSize: '13px',
    lineHeight: '1.5',
    whiteSpace: 'pre',
  },
  logActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logCount: {
    color: '#8892b0',
    fontSize: '14px',
  },
  toggleButton: {
    padding: '4px 12px',
    borderRadius: '4px',
    border: '1px solid #8892b0',
    backgroundColor: 'transparent',
    color: '#8892b0',
    cursor: 'pointer',
    fontSize: '12px',
  },
  clearButton: {
    padding: '4px 12px',
    borderRadius: '4px',
    border: '1px solid #f44336',
    backgroundColor: 'transparent',
    color: '#f44336',
    cursor: 'pointer',
    fontSize: '12px',
  },
  logsContainer: {
    backgroundColor: '#0d1117',
    borderRadius: '8px',
    padding: '12px',
    maxHeight: '300px',
    overflowY: 'auto',
    border: '1px solid #233554',
    fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
    fontSize: '12px',
  },
  emptyLogs: {
    color: '#8892b0',
    textAlign: 'center',
    padding: '24px',
  },
  logEntry: {
    display: 'flex',
    gap: '8px',
    padding: '4px 0',
    borderBottom: '1px solid rgba(35, 53, 84, 0.5)',
  },
  logTime: {
    color: '#6e7681',
    minWidth: '80px',
    flexShrink: 0,
  },
  logLevel: {
    fontWeight: 'bold',
    minWidth: '70px',
    flexShrink: 0,
  },
  logMessage: {
    color: '#c9d1d9',
    wordBreak: 'break-word',
  },
};
